import React from "react";
import { TrendingUp, TrendingDown, X } from "lucide-react";
import { getOrderTimeDetails, getProfitPercentage } from "../utils/earlyCloseUtils";
import { formatTime, roundToCents, formatUsd } from "../utils/upDownFormatters";

export function UpDownActiveTradesList({
  activeOrders = [],
  orderStickyEndTimes,
  clockNow,
  chartRef,
  livePrice,
  wallet,
  dynamicPayoutPercent,
  userRank,
  effectivePayoutMultiplier,
  setCancelOrderId,
}) {
  if (!activeOrders || activeOrders.length === 0) {
    return null;
  }

  return (
    <div className="updown-active-trades">
      {activeOrders.map((order) => {
        const stickyEnd = orderStickyEndTimes?.get(order.id);
        const timeDetails = getOrderTimeDetails(
          order,
          stickyEnd,
          clockNow,
        );
        if (orderStickyEndTimes && !orderStickyEndTimes.has(order.id) && timeDetails.endMs) {
          orderStickyEndTimes.set(order.id, timeDetails.endMs);
        }

        const {
          remainingSeconds: remainingSecondsForOrder,
          progressPercentage,
        } = timeDetails;

        const isActiveOrder = order.status === "active";
        const isSettlingOrder = order.status === "settling";
        const isWonOrder = order.status === "won";
        const isLostOrder =
          order.status === "lost" ||
          order.status === "loss" ||
          order.status === "lose";

        const entryPrice = Number(order.entryPrice);
        const currentPriceValue = Number(chartRef?.current?.getCurrentPrice?.());
        const currentPrice =
          Number.isFinite(currentPriceValue) && currentPriceValue > 0
            ? currentPriceValue
            : Number(livePrice);
        const isDraw =
          Number.isFinite(currentPrice) &&
          Number.isFinite(entryPrice) &&
          currentPrice === entryPrice;

        const isWinning = isWonOrder
          ? true
          : isLostOrder
            ? false
            : (isActiveOrder || isSettlingOrder)
              ? Number.isFinite(currentPrice) &&
                Number.isFinite(entryPrice) &&
                ((currentPrice > entryPrice && order.side === "up") ||
                  (currentPrice < entryPrice && order.side === "down"))
              : false;

        const profitPercent = Number(
          getProfitPercentage(
            progressPercentage,
            wallet?.binary?.profit_details,
            wallet?.binary?.win_percentage || dynamicPayoutPercent,
            userRank,
            isWinning,
          ),
        );

        const statusLabel = isActiveOrder
          ? formatTime(remainingSecondsForOrder)
          : isSettlingOrder
            ? "..."
            : "";

        const resultValue = isActiveOrder
          ? isDraw
            ? "$0.00"
            : (() => {
                if (timeDetails.durationSeconds >= 30) {
                  const amountValue = Number(order.amount || 0);
                  if (isWinning) {
                    const closeProfit = roundToCents((amountValue * profitPercent) / 100);
                    if (closeProfit === 0) return "$0.00";
                    return `+${formatUsd(Math.abs(closeProfit))}`;
                  } else {
                    const absLoss = Math.abs(profitPercent);
                    const closeProfit = roundToCents((amountValue * absLoss) / 100);
                    if (closeProfit === 0) return "$0.00";
                    return `-${formatUsd(Math.abs(closeProfit))}`;
                  }
                }
                const payout = Number(order.payoutMultiplier || effectivePayoutMultiplier || 1.82);
                const orderMult = Number(
                  order.tradeMultiplier ??
                    (order.isLuckyRound && Number(order.jackpot_multiplier) > 1
                      ? order.jackpot_multiplier
                      : (order.appliedMultiplier || order.jackpot_multiplier || 1)),
                );
                const effectiveOrderMult = orderMult > 1 ? orderMult : 1;
                const p = isWinning
                  ? Number(order.amount || 0) * (payout - 1) * effectiveOrderMult
                  : -Number(order.amount || 0);
                if (p === 0) return "$0.00";
                const absP = Math.abs(p);
                const s4 = absP.toFixed(4);
                const s3 = absP.toFixed(3);
                const s2 = absP.toFixed(2);
                const formatted =
                  Number(s4) !== Number(s3)
                    ? s4
                    : Number(s3) !== Number(s2)
                      ? s3
                      : s2;
                return p > 0 ? `+$${formatted}` : `-$${formatted}`;
              })()
          : isSettlingOrder
            ? "..."
            : (() => {
                let p = 0;
                if (order.profit !== undefined) {
                  p = Number(order.profit);
                } else {
                  const payout = Number(order.payoutMultiplier || effectivePayoutMultiplier);
                  const orderMult = Number(
                    order.tradeMultiplier ??
                      (order.isLuckyRound && Number(order.jackpot_multiplier) > 1
                        ? order.jackpot_multiplier
                        : (order.appliedMultiplier || order.jackpot_multiplier || 1)),
                  );
                  const effectiveOrderMult = orderMult > 1 ? orderMult : 1;
                  p = isDraw
                    ? 0
                    : (isWinning || isWonOrder)
                      ? Number(order.amount || 0) * (payout - 1) * effectiveOrderMult
                      : -Number(order.amount || 0);
                }
                if (isDraw || p === 0) return "$0.00";
                const absP = Math.abs(p);
                const s4 = absP.toFixed(4);
                const s3 = absP.toFixed(3);
                const s2 = absP.toFixed(2);
                const formatted =
                  Number(s4) !== Number(s3)
                    ? s4
                    : Number(s3) !== Number(s2)
                      ? s3
                      : s2;
                return p > 0 ? `+$${formatted}` : `-$${formatted}`;
              })();

        const isTension = isActiveOrder && remainingSecondsForOrder > 0 && remainingSecondsForOrder <= 3;

        return (
          <div
            key={order.id}
            className={`updown-active-trade ${isWinning ? "is-winning" : "is-losing"} ${isTension ? "is-tension" : ""}`}
          >
            <div className="updown-active-trade__pill">
              <div className="updown-active-trade__track" aria-hidden="true">
                <div
                  className="updown-active-trade__laser-bar"
                  style={{ width: `${Math.max(0, 100 - progressPercentage)}%` }}
                />
              </div>
              <span className="updown-active-trade__time relative z-10">
                {statusLabel}
              </span>
              {order.side === "up" ? (
                <TrendingUp
                  size={12}
                  strokeWidth={2.8}
                  className="updown-active-trade__arrow relative z-10"
                  aria-hidden="true"
                />
              ) : (
                <TrendingDown
                  size={12}
                  strokeWidth={2.8}
                  className="updown-active-trade__arrow relative z-10"
                  aria-hidden="true"
                />
              )}
            </div>
            {timeDetails.durationSeconds >= 30 && (
              <button
                type="button"
                className="updown-active-trade__close"
                aria-label="Early close trade"
                title="Early Close Trade"
                onClick={() => setCancelOrderId(order.id)}
              >
                <X size={10} strokeWidth={3} className="shrink-0" />
              </button>
            )}
            <span className="updown-active-trade__result">
              {resultValue}
            </span>
          </div>
        );
      })}
    </div>
  );
}

